import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Sparkles,
  Trash2,
  Save,
  X,
  ExternalLink,
  Loader2,
  Wand2,
  RefreshCw,
} from "lucide-react";

const INTENTS = [
  { id: "retire", label: "Retire" },
  { id: "relocate", label: "Relocate" },
];
const COUNTRIES = ["Spain", "Portugal", "Italy"];

export function SeoPanel({ axios }) {
  const [pages, setPages] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // page object being edited
  const [showGen, setShowGen] = useState(false);

  function load() {
    setLoading(true);
    Promise.all([
      axios.get("/admin/seo-pages"),
      axios.get("/admin/seo-jobs"),
    ])
      .then(([p, j]) => {
        setPages(p.data || []);
        setJobs(j.data || []);
      })
      .catch(() => toast.error("Failed to load SEO pages."))
      .finally(() => setLoading(false));
  }

  useEffect(load, [axios]);

  // Poll running jobs every 5s while any job is in progress
  const hasRunning = jobs.some((j) => j.status === "running");
  useEffect(() => {
    if (!hasRunning) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRunning]);

  const deletePage = async (slug) => {
    if (!window.confirm(`Delete SEO page "${slug}"? This cannot be undone.`))
      return;
    try {
      await axios.delete(`/admin/seo-pages/${slug}`);
      toast.success("Page deleted.");
      setPages((p) => p.filter((x) => x.slug !== slug));
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="seo-panel" className="space-y-10">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl tracking-tight">SEO pages</h2>
          <p className="text-[#6B4954] text-sm mt-2 leading-relaxed max-w-2xl">
            Generate ranking pages from a list of keywords using Claude Sonnet 4.5.
            Generated pages auto-publish and appear on the sitemap. Edit anything
            after generation.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="seo-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 border border-[#451C25]/30 px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#451C25] hover:text-white transition-colors"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            data-testid="seo-open-generator"
            onClick={() => setShowGen(true)}
            className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#2B1017] transition-colors"
          >
            <Wand2 size={14} /> Generate with AI
          </button>
        </div>
      </header>

      {/* Recent jobs (only if any) */}
      {jobs.length > 0 && (
        <section data-testid="seo-jobs-list">
          <h3 className="text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-3">
            Recent generations
          </h3>
          <div className="space-y-3">
            {jobs.slice(0, 5).map((j) => (
              <JobRow key={j.id} job={j} />
            ))}
          </div>
        </section>
      )}

      {/* Pages */}
      <section data-testid="seo-pages-list">
        <h3 className="text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-3">
          {pages.length} published pages
        </h3>
        {loading ? (
          <div className="text-[#6B4954] text-sm py-12">Loading...</div>
        ) : pages.length === 0 ? (
          <div className="border border-dashed border-[#451C25]/20 p-10 text-center text-[#6B4954] text-sm">
            No SEO pages yet. Click <strong>Generate with AI</strong> to create
            your first batch.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pages.map((p) => (
              <PageCard
                key={p.slug}
                page={p}
                onEdit={() => setEditing(p)}
                onDelete={() => deletePage(p.slug)}
              />
            ))}
          </div>
        )}
      </section>

      {showGen && (
        <GeneratorModal
          axios={axios}
          onClose={() => setShowGen(false)}
          onCreated={() => {
            setShowGen(false);
            load();
          }}
        />
      )}

      {editing && (
        <EditModal
          axios={axios}
          page={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setPages((prev) =>
              prev.map((p) => (p.slug === saved.slug ? saved : p))
            );
          }}
        />
      )}
    </div>
  );
}

function JobRow({ job }) {
  const pct = job.total ? Math.round(((job.created + job.failed) / job.total) * 100) : 0;
  const isDone = job.status === "done";
  return (
    <div
      data-testid={`seo-job-${job.id}`}
      className="bg-white border border-[#451C25]/10 p-4"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-[12px] tracking-[0.18em] uppercase text-[#451C25]">
          {job.intent} · {job.country} · {job.total} keyword
          {job.total !== 1 ? "s" : ""}
        </div>
        <div
          className={`text-[10px] tracking-[0.22em] uppercase px-2 py-1 ${
            isDone
              ? job.failed === 0
                ? "bg-[#183129]/15 text-[#0D231B]"
                : "bg-[#451C25]/15 text-[#2B1017]"
              : "bg-[#451C25]/10 text-[#451C25]"
          }`}
        >
          {isDone ? `Done · ${job.created}/${job.total}` : `Running · ${pct}%`}
        </div>
      </div>
      {!isDone && (
        <div className="h-1 bg-[#451C25]/10 mb-3">
          <div
            className="h-full bg-[#451C25] transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      <ul className="space-y-1">
        {job.items.map((it, i) => (
          <li
            key={i}
            className="flex items-center gap-2 text-[12px] text-[#6B4954]"
          >
            {it.status === "ok" && (
              <span className="text-[#0D231B]">✓</span>
            )}
            {it.status === "error" && (
              <span className="text-[#2B1017]">✗</span>
            )}
            {it.status === "running" && (
              <Loader2 size={11} className="animate-spin text-[#451C25]" />
            )}
            {it.status === "pending" && (
              <span className="text-[#6B4954]/60">○</span>
            )}
            <span className="flex-1 truncate">{it.keyword}</span>
            {it.slug && (
              <code className="text-[10px] text-[#6B4954]">{it.slug}</code>
            )}
            {it.error && (
              <span className="text-[10px] text-[#2B1017] truncate max-w-[50%]">
                {it.error}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PageCard({ page, onEdit, onDelete }) {
  return (
    <article
      data-testid={`seo-page-card-${page.slug}`}
      className="bg-white border border-[#451C25]/10 p-5 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#451C25]">
          {page.intent} · {page.country}
        </div>
        <div className="flex items-center gap-2">
          <a
            data-testid={`seo-page-preview-${page.slug}`}
            href={`/${page.slug}`}
            target="_blank"
            rel="noreferrer"
            className="text-[#6B4954] hover:text-[#451C25]"
            title="Preview"
          >
            <ExternalLink size={14} />
          </a>
          <button
            data-testid={`seo-page-edit-${page.slug}`}
            onClick={onEdit}
            className="text-[10px] tracking-[0.22em] uppercase text-[#6B4954] hover:text-[#451C25]"
          >
            Edit
          </button>
          <button
            data-testid={`seo-page-delete-${page.slug}`}
            onClick={onDelete}
            className="text-[#6B4954] hover:text-[#2B1017]"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <h4 className="font-serif text-xl tracking-tight leading-snug">
        {page.h1}
      </h4>
      <p className="text-[#6B4954] text-[13px] leading-relaxed line-clamp-2">
        {page.meta_description}
      </p>
      <div className="text-[10px] text-[#6B4954]/70">
        /{page.slug} · {page.sections?.length || 0} sections ·{" "}
        {page.faq?.length || 0} FAQs · {page.published ? "Published" : "Draft"}
      </div>
    </article>
  );
}

function GeneratorModal({ axios, onClose, onCreated }) {
  const [keywords, setKeywords] = useState("");
  const [intent, setIntent] = useState("retire");
  const [country, setCountry] = useState("Spain");
  const [publish, setPublish] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const list = keywords
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    if (list.length === 0) {
      toast.error("Enter at least one keyword.");
      return;
    }
    if (list.length > 20) {
      toast.error("Max 20 keywords per batch.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post("/admin/seo-pages/generate", {
        keywords: list,
        intent,
        country,
        publish,
      });
      toast.success(
        `Generation started for ${res.data.total} keyword${res.data.total !== 1 ? "s" : ""}. This runs in the background.`
      );
      onCreated();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to start generation.";
      toast.error(typeof msg === "string" ? msg : "Failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="seo-generator-modal"
      className="fixed inset-0 z-50 bg-[#451C25]/40 backdrop-blur-sm flex items-end md:items-center justify-center"
    >
      <div className="bg-[#F5EFE7] w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-[#451C25]/15">
        <header className="flex items-center justify-between px-6 py-5 border-b border-[#451C25]/10">
          <div className="flex items-center gap-3">
            <Sparkles size={18} className="text-[#451C25]" />
            <h3 className="font-serif text-2xl tracking-tight">
              Generate SEO pages
            </h3>
          </div>
          <button
            data-testid="seo-generator-close"
            onClick={onClose}
            className="text-[#6B4954] hover:text-[#451C25]"
          >
            <X size={18} />
          </button>
        </header>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
              Keywords (one per line, max 20)
            </label>
            <textarea
              data-testid="seo-generator-keywords"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              rows={8}
              placeholder={`Beckham Law for American expats\nD7 visa Portugal passive income\nElective Residency Visa Italy retirees`}
              className="w-full bg-white border border-[#451C25]/20 px-3 py-3 text-sm focus:outline-none focus:border-[#451C25] font-mono leading-relaxed"
            />
            <p className="mt-2 text-[11px] text-[#6B4954]">
              Each keyword becomes one full SEO landing page (≈ 25-40s each).
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
                Intent
              </label>
              <div className="grid grid-cols-2 gap-2">
                {INTENTS.map((i) => (
                  <button
                    key={i.id}
                    data-testid={`seo-generator-intent-${i.id}`}
                    onClick={() => setIntent(i.id)}
                    className={`py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
                      intent === i.id
                        ? "bg-[#451C25] text-white border-[#451C25]"
                        : "border-[#451C25]/25 text-[#451C25] hover:border-[#451C25]"
                    }`}
                  >
                    {i.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
                Country
              </label>
              <select
                data-testid="seo-generator-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full bg-white border border-[#451C25]/20 py-2 px-3 text-sm focus:outline-none focus:border-[#451C25]"
              >
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-3 text-sm text-[#451C25]">
            <input
              data-testid="seo-generator-publish"
              type="checkbox"
              checked={publish}
              onChange={(e) => setPublish(e.target.checked)}
              className="accent-[#451C25]"
            />
            Publish immediately (uncheck to save as draft)
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button
              data-testid="seo-generator-cancel"
              onClick={onClose}
              className="px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase text-[#6B4954] hover:text-[#451C25]"
            >
              Cancel
            </button>
            <button
              data-testid="seo-generator-submit"
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-[#451C25] text-white px-6 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#2B1017] disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Wand2 size={14} />
              )}
              Start generation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditModal({ axios, page, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    slug: page.slug,
    intent: page.intent,
    country: page.country,
    meta_title: page.meta_title,
    meta_description: page.meta_description,
    og_image: page.og_image || "",
    h1: page.h1,
    sub: page.sub,
    target_keywords: (page.target_keywords || []).join(", "),
    sections: (page.sections || []).map((s) => ({ ...s })),
    faq: (page.faq || []).map((f) => ({ ...f })),
    published: !!page.published,
  }));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateSection = (idx, key, val) =>
    setForm((f) => {
      const next = [...f.sections];
      next[idx] = { ...next[idx], [key]: val };
      return { ...f, sections: next };
    });
  const addSection = () =>
    setForm((f) => ({ ...f, sections: [...f.sections, { h2: "", body: "" }] }));
  const removeSection = (idx) =>
    setForm((f) => ({
      ...f,
      sections: f.sections.filter((_, i) => i !== idx),
    }));

  const updateFaq = (idx, key, val) =>
    setForm((f) => {
      const next = [...f.faq];
      next[idx] = { ...next[idx], [key]: val };
      return { ...f, faq: next };
    });
  const addFaq = () =>
    setForm((f) => ({ ...f, faq: [...f.faq, { q: "", a: "" }] }));
  const removeFaq = (idx) =>
    setForm((f) => ({ ...f, faq: f.faq.filter((_, i) => i !== idx) }));

  const save = async () => {
    const payload = {
      slug: form.slug.trim(),
      intent: form.intent,
      country: form.country,
      meta_title: form.meta_title.trim(),
      meta_description: form.meta_description.trim(),
      og_image: form.og_image.trim() || null,
      h1: form.h1.trim(),
      sub: form.sub.trim(),
      target_keywords: form.target_keywords
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      sections: form.sections
        .filter((s) => s.h2.trim() && s.body.trim())
        .map((s) => ({ h2: s.h2.trim(), body: s.body })),
      faq: form.faq
        .filter((f) => f.q.trim() && f.a.trim())
        .map((f) => ({ q: f.q.trim(), a: f.a })),
      published: form.published,
    };
    setSaving(true);
    try {
      const res = await axios.put(`/admin/seo-pages/${page.slug}`, payload);
      toast.success("Page saved.");
      onSaved(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed.";
      toast.error(typeof msg === "string" ? msg : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="seo-edit-modal"
      className="fixed inset-0 z-50 bg-[#451C25]/40 backdrop-blur-sm flex items-end md:items-center justify-center"
    >
      <div className="bg-[#F5EFE7] w-full max-w-4xl max-h-[94vh] overflow-y-auto border border-[#451C25]/15">
        <header className="sticky top-0 z-10 bg-[#F5EFE7] flex items-center justify-between px-6 py-5 border-b border-[#451C25]/10">
          <h3 className="font-serif text-2xl tracking-tight">Edit SEO page</h3>
          <div className="flex items-center gap-3">
            <button
              data-testid="seo-edit-close"
              onClick={onClose}
              className="text-[#6B4954] hover:text-[#451C25]"
            >
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field
              label="Slug"
              value={form.slug}
              onChange={(v) => set("slug", v)}
              testid="seo-edit-slug"
              disabled
            />
            <SelectField
              label="Intent"
              value={form.intent}
              onChange={(v) => set("intent", v)}
              options={[
                { v: "retire", l: "Retire" },
                { v: "relocate", l: "Relocate" },
              ]}
              testid="seo-edit-intent"
            />
            <SelectField
              label="Country"
              value={form.country}
              onChange={(v) => set("country", v)}
              options={COUNTRIES.map((c) => ({ v: c, l: c }))}
              testid="seo-edit-country"
            />
          </div>

          <Field
            label="Meta title"
            value={form.meta_title}
            onChange={(v) => set("meta_title", v)}
            testid="seo-edit-meta-title"
            hint={`${form.meta_title.length} chars (ideal 55-65)`}
          />
          <Field
            label="Meta description"
            value={form.meta_description}
            onChange={(v) => set("meta_description", v)}
            multiline
            testid="seo-edit-meta-desc"
            hint={`${form.meta_description.length} chars (ideal 150-160)`}
          />
          <Field
            label="H1"
            value={form.h1}
            onChange={(v) => set("h1", v)}
            testid="seo-edit-h1"
          />
          <Field
            label="Sub (hero subtitle)"
            value={form.sub}
            onChange={(v) => set("sub", v)}
            multiline
            testid="seo-edit-sub"
          />
          <Field
            label="OG image URL (optional)"
            value={form.og_image}
            onChange={(v) => set("og_image", v)}
            testid="seo-edit-og"
          />
          <Field
            label="Target keywords (comma separated)"
            value={form.target_keywords}
            onChange={(v) => set("target_keywords", v)}
            testid="seo-edit-keywords"
          />

          {/* Sections */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-[11px] tracking-[0.22em] uppercase text-[#6B4954]">
                Sections ({form.sections.length})
              </h4>
              <button
                data-testid="seo-edit-add-section"
                onClick={addSection}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#451C25] hover:text-[#2B1017]"
              >
                <Plus size={12} /> Add section
              </button>
            </div>
            <div className="space-y-4">
              {form.sections.map((s, i) => (
                <div
                  key={i}
                  data-testid={`seo-edit-section-${i}`}
                  className="bg-white border border-[#451C25]/10 p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#6B4954]">
                      Section {i + 1}
                    </span>
                    <button
                      onClick={() => removeSection(i)}
                      className="text-[#6B4954] hover:text-[#2B1017]"
                      data-testid={`seo-edit-remove-section-${i}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <input
                    value={s.h2}
                    onChange={(e) => updateSection(i, "h2", e.target.value)}
                    placeholder="H2 title"
                    className="w-full bg-[#F5EFE7] border border-[#451C25]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25] font-serif"
                  />
                  <textarea
                    value={s.body}
                    onChange={(e) => updateSection(i, "body", e.target.value)}
                    rows={8}
                    placeholder="Body (markdown / paragraphs separated by blank lines)"
                    className="w-full bg-[#F5EFE7] border border-[#451C25]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25] leading-relaxed"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-[11px] tracking-[0.22em] uppercase text-[#6B4954]">
                FAQ ({form.faq.length})
              </h4>
              <button
                data-testid="seo-edit-add-faq"
                onClick={addFaq}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#451C25] hover:text-[#2B1017]"
              >
                <Plus size={12} /> Add FAQ
              </button>
            </div>
            <div className="space-y-3">
              {form.faq.map((f, i) => (
                <div
                  key={i}
                  data-testid={`seo-edit-faq-${i}`}
                  className="bg-white border border-[#451C25]/10 p-4 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#6B4954]">
                      Q {i + 1}
                    </span>
                    <button
                      onClick={() => removeFaq(i)}
                      className="text-[#6B4954] hover:text-[#2B1017]"
                      data-testid={`seo-edit-remove-faq-${i}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <input
                    value={f.q}
                    onChange={(e) => updateFaq(i, "q", e.target.value)}
                    placeholder="Question"
                    className="w-full bg-[#F5EFE7] border border-[#451C25]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25]"
                  />
                  <textarea
                    value={f.a}
                    onChange={(e) => updateFaq(i, "a", e.target.value)}
                    rows={3}
                    placeholder="Answer"
                    className="w-full bg-[#F5EFE7] border border-[#451C25]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25]"
                  />
                </div>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-3 text-sm text-[#451C25]">
            <input
              data-testid="seo-edit-published"
              type="checkbox"
              checked={form.published}
              onChange={(e) => set("published", e.target.checked)}
              className="accent-[#451C25]"
            />
            Published (appears on sitemap & public site)
          </label>
        </div>

        <footer className="sticky bottom-0 bg-[#F5EFE7] border-t border-[#451C25]/10 px-6 py-4 flex justify-end gap-3">
          <button
            data-testid="seo-edit-cancel"
            onClick={onClose}
            className="px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase text-[#6B4954] hover:text-[#451C25]"
          >
            Cancel
          </button>
          <button
            data-testid="seo-edit-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#451C25] text-white px-6 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#3a3f33] disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save changes
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, multiline, hint, testid, disabled }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
        {label}
      </label>
      {multiline ? (
        <textarea
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={3}
          className="w-full bg-white border border-[#451C25]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25] disabled:opacity-60"
        />
      ) : (
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full bg-white border border-[#451C25]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25] disabled:opacity-60"
        />
      )}
      {hint && (
        <p className="mt-1 text-[10px] text-[#6B4954]">{hint}</p>
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
        {label}
      </label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#451C25]/20 py-2 px-3 text-sm focus:outline-none focus:border-[#451C25]"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </div>
  );
}
